import { Box, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TooltipRenderProps } from 'react-joyride';

/**
 * Custom react-joyride tooltip built entirely from MUI components so it
 * follows the active theme (light/dark) automatically.
 *
 * Steps can set `data: { hideFooter: true }` to suppress the default
 * Skip/Back/Next footer and provide their own action buttons inside
 * `content` (used by the single-step first-episode celebration).
 */
export const TourTooltip = ({
  backProps,
  index,
  isLastStep,
  primaryProps,
  size,
  skipProps,
  step,
  tooltipProps,
}: TooltipRenderProps) => {
  const { t } = useTranslation('common');
  const hideFooter = Boolean(
    (step.data as { hideFooter?: boolean } | undefined)?.hideFooter
  );

  return (
    <Paper
      {...tooltipProps}
      elevation={8}
      sx={{ borderRadius: 2, maxWidth: 380, p: 2 }}
    >
      {step.title && (
        <Typography variant="h6" component="div" gutterBottom>
          {step.title}
        </Typography>
      )}
      <Typography variant="body2" component="div" color="text.secondary">
        {step.content}
      </Typography>

      {!hideFooter && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <Button {...skipProps} color="inherit" size="small">
            {t('tour.skip', 'Skip')}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {size > 1 && (
            <Typography variant="caption" color="text.secondary">
              {index + 1} / {size}
            </Typography>
          )}
          {index > 0 && (
            <Button {...backProps} size="small">
              {t('tour.back', 'Back')}
            </Button>
          )}
          <Button {...primaryProps} variant="contained" size="small">
            {isLastStep ? t('tour.done', 'Done') : t('tour.next', 'Next')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
